import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PRIORITY_BORDER = {
  urgent: 'border-red-500', high: 'border-orange-500', low: 'border-slate-400', normal: 'border-blue-500',
};

function useNotifications(enabled) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setLoading(false); return; }

      const [notificationsResponse, announcementsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/club-announcements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);

      const allNotifications = [];
      const notifList = Array.isArray(notificationsResponse.data)
        ? notificationsResponse.data
        : (notificationsResponse.data?.announcements || []);
      notifList.forEach((notification) => {
        if (notification.is_active && (!notification.expires_at || new Date(notification.expires_at) > new Date())) {
          allNotifications.push({ ...notification, type: 'notification', is_read: notification.is_read || false });
        }
      });

      if (Array.isArray(announcementsResponse.data)) {
        announcementsResponse.data.forEach((announcement) => {
          if (announcement.is_active && (!announcement.expires_at || new Date(announcement.expires_at) > new Date())) {
            allNotifications.push({
              id: `announcement_${announcement.id}`,
              title: announcement.title,
              content: announcement.content,
              priority: announcement.priority || 'normal',
              type: 'announcement',
              created_at: announcement.created_at,
              expires_at: announcement.expires_at,
              is_read: false,
            });
          }
        });
      }

      allNotifications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(allNotifications);
    } catch (error) {
      console.error('خطأ في جلب الإشعارات:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [enabled]);

  const markAsRead = async (notification) => {
    setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)));
    if (notification.type === 'notification') {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API_BASE_URL}/api/notifications/mark-read`, { notification_id: notification.id }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        console.error('خطأ في تحديث حالة الإشعار:', error);
      }
    }
  };

  return { notifications, loading, markAsRead };
}

export const NotificationBell = ({ user, align = 'left', dropdownDirection = 'down' }) => {
  const { notifications, loading, markAsRead } = useNotifications(!!user);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  if (loading || !user) return null;
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const sideCls = align === 'right' ? 'right-0' : 'left-0';
  const verticalCls = dropdownDirection === 'up' ? 'bottom-11' : 'top-11';

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-wrapper">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-all hover:bg-slate-100/80 hover:text-teal-700 active:scale-90"
        data-testid="notification-bell-btn"
        title="التنبيهات"
      >
        <svg viewBox="0 0 24 24" fill="none" className="w-4.5 h-4.5" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -left-1 flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white" data-testid="notification-unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute ${verticalCls} ${sideCls} w-80 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl`} data-testid="notification-dropdown-panel">
          <div className="sticky top-0 border-b border-slate-100 bg-white px-4 py-2.5 text-sm font-bold text-slate-800">التنبيهات</div>
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400" data-testid="notification-empty-state">لا توجد تنبيهات حالياً</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <div key={n.id} className={`border-r-4 ${PRIORITY_BORDER[n.priority] || PRIORITY_BORDER.normal} px-4 py-3 ${n.is_read ? 'opacity-60' : ''}`} data-testid={`notification-item-${n.id}`}>
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-800">{n.title}</h4>
                    {!n.is_read && (
                      <button onClick={() => markAsRead(n)} className="shrink-0 text-[11px] text-teal-600 hover:underline" data-testid={`notification-mark-read-${n.id}`}>تعليم كمقروء</button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{n.content}</p>
                  {n.created_at && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      {new Date(n.created_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
